import { MarketsSort, MarketsSortField, ProtocolKey } from "@silo/types";
import { cn } from "../../utils/class-names";
import {
  LiquidityCell,
  MarketCell,
  MarketYieldsCell,
  BaseRow,
  sortButton,
} from "./shared";
import { match } from "ts-pattern";
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from "../../icons";
import { ReactNode } from "react";
import { AprSortHeader } from "./apr-sort-header";
import { useElementSize } from "../../hooks/use-element-size";
import { getProtocolConfig } from "@silo/config";
import { isNotNullable } from "effect/Predicate";

interface HeadProps {
  className?: string;
  marketsSort: MarketsSort;
  sortBy: (field: MarketsSortField) => void;
  protocolKey: ProtocolKey;
}

export function Head({
  className,
  marketsSort,
  sortBy,
  protocolKey,
}: HeadProps): JSX.Element {
  /**
   * These ref and resize observers cause all the APY headers to wrap at the same time by using nested spans.
   *
   * TODO: come up with a pure CSS solution or abstraction for this logic if it is repeated elsewhere
   */
  const { ref: baseRef, width: baseWidth } = useElementSize();
  const { ref: firstBridgeRef, width: firstBridgeWidth } = useElementSize();
  const { ref: secondBridgeRef, width: secondBridgeWidth } = useElementSize();
  const width = `${Math.max(baseWidth, firstBridgeWidth, secondBridgeWidth)}px`;

  const { firstBridgeSymbol, secondBridgeSymbol } =
    getProtocolConfig(protocolKey);

  return (
    <BaseRow className={cn("text-shale-200 text-xs", className)}>
      <MarketCell>
        <SortHeader
          marketsSort={marketsSort}
          field="marketSymbol"
          sortBy={sortBy}
        >
          Market
        </SortHeader>
      </MarketCell>

      <LiquidityCell>
        <SortHeader
          marketsSort={marketsSort}
          field="liquidityUsd"
          sortBy={sortBy}
        >
          Available to borrow
        </SortHeader>
      </LiquidityCell>

      <MarketYieldsCell protocolKey={protocolKey}>
        <AprSortHeader
          labelContent="Base"
          labelRef={baseRef}
          labelWidth={width}
          depositField="baseDepositTotalApr"
          debtField="baseDebtTotalApr"
          marketsSort={marketsSort}
          sortBy={sortBy}
        />

        <AprSortHeader
          labelContent={firstBridgeSymbol}
          labelRef={firstBridgeRef}
          labelWidth={width}
          depositField="firstBridgeDepositTotalApr"
          debtField="firstBridgeDebtTotalApr"
          marketsSort={marketsSort}
          sortBy={sortBy}
        />

        {isNotNullable(secondBridgeSymbol) ? (
          <AprSortHeader
            labelContent={secondBridgeSymbol}
            labelRef={secondBridgeRef}
            labelWidth={width}
            depositField="secondBridgeDepositTotalApr"
            debtField="secondBridgeDebtTotalApr"
            marketsSort={marketsSort}
            sortBy={sortBy}
          />
        ) : null}
      </MarketYieldsCell>
    </BaseRow>
  );
}

interface SortHeaderProps {
  marketsSort: MarketsSort;
  field: MarketsSortField;
  sortBy: (field: MarketsSortField) => void;
  children?: ReactNode;
}

function SortHeader({
  marketsSort,
  field,
  sortBy,
  children,
}: SortHeaderProps): JSX.Element {
  return (
    <button
      className={sortButton({
        className: "flex items-center gap-2",
        isActive: marketsSort?.field === field,
      })}
      onClick={() => {
        sortBy(field);
      }}
    >
      <span>{children}</span>

      <span>
        {match(marketsSort)
          .with({ field, direction: "desc" }, () => (
            <ArrowDownIcon className="w-4 h-4" />
          ))
          .with({ field, direction: "asc" }, () => (
            <ArrowUpIcon className="w-4 h-4" />
          ))
          .otherwise(() => (
            <ArrowUpDownIcon className="w-4 h-4" />
          ))}
      </span>
    </button>
  );
}
